import 'react-day-picker/style.css'
import { Flex, Input, Stack } from '@chakra-ui/react'
import { Global, css } from '@emotion/react'
import { i18n } from '@lingui/core'
import { format, isDate } from 'date-fns'
import { type FC, useRef, useState } from 'react'
import type { DayPickerProps, Modifiers } from 'react-day-picker'

import LocalizedDayPicker from './LocalizedDayPicker'
import NavBar from './NavBar'
import createStyle from './style'

const CLS_NAME = 'capturi-dp'
const cssStyle = createStyle(CLS_NAME)

export type DatePickerProps = DayPickerProps & {
  // Controlled value
  value?: Date
  defaultValue?: Date
  onSelectDate?: (date: Date) => void
  inputPlaceholder?: string
  showInputs?: boolean
}

export function defaultFormat(d: Date): string {
  return isDate(d) ? format(d, 'yyyy-MM-dd') : ''
}

const DatePicker: FC<DatePickerProps> = ({
  value: controlledValue,
  defaultValue,
  onSelectDate,
  inputPlaceholder = '',
  showInputs = false,
  disabled,
}) => {
  const { current: isControlled } = useRef(controlledValue != null)

  const dateInputRef = useRef<HTMLInputElement>(null)

  const [value, setValue] = useState<Date | undefined>(defaultValue)
  const _value = isControlled && controlledValue ? controlledValue : value
  const [month, setMonth] = useState<Date>(_value ?? new Date())
  const dateInputValue = _value
    ? i18n.date(_value, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      })
    : undefined

  const handleDayClick = (
    _day: Date | undefined,
    selectedDay: Date,
    activeModifiers: Modifiers,
  ): void => {
    if (activeModifiers.disabled) {
      return
    }

    setValue(selectedDay)
    onSelectDate?.(selectedDay)
  }

  // Page to the selected month
  const pageToMonth = (date?: Date): void => {
    if (date) {
      setMonth(date)
    }
  }

  const handleInputFocus = (): void => {
    pageToMonth(_value)
  }

  return (
    <Flex alignItems="center" flexDir="column" overflow="auto">
      {showInputs && (
        <Stack
          as="header"
          direction="row"
          spacing={[2, 4]}
          alignItems="center"
          p={[2, 4]}
          maxW="273px"
        >
          <Input
            ref={dateInputRef}
            size="sm"
            value={dateInputValue}
            onFocus={handleInputFocus}
            placeholder={inputPlaceholder}
            isReadOnly
            minW={0}
            flex={1}
          />
        </Stack>
      )}
      <LocalizedDayPicker
        className={CLS_NAME}
        mode="single"
        onSelect={handleDayClick}
        selected={_value}
        components={{ MonthCaption: (p) => <NavBar {...p} /> }}
        month={month}
        onMonthChange={setMonth}
        disabled={disabled}
        weekStartsOn={1}
      />
      <Global
        styles={css`
          ${cssStyle}
        `}
      />
    </Flex>
  )
}

export { DatePicker }
